import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Collapse,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';

// Icons
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ApiIcon from '@mui/icons-material/Api';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MemoryIcon from '@mui/icons-material/Memory';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import SecurityIcon from '@mui/icons-material/Security';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuIcon from '@mui/icons-material/Menu';

// Documentation sections structure
const docSections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: <MenuBookIcon />,
    subsections: [
      { id: 'introduction', title: 'Introduction' },
      { id: 'installation', title: 'Installation' },
      { id: 'quick-start', title: 'Quick Start Guide' },
      { id: 'key-concepts', title: 'Key Concepts' }
    ]
  },
  {
    id: 'platform-overview',
    title: 'Platform Overview',
    icon: <BuildIcon />,
    subsections: [
      { id: 'dashboard', title: 'Dashboard' },
      { id: 'technical-analysis', title: 'Technical Analysis' },
      { id: 'fundamental-analysis', title: 'Fundamental Analysis' },
      { id: 'risk-management', title: 'Risk Management' },
      { id: 'trade-history', title: 'Trade History' },
      { id: 'execution', title: 'Execution' }
    ]
  },
  {
    id: 'strategy-builder',
    title: 'Strategy Builder',
    icon: <AccountTreeIcon />,
    subsections: [
      { id: 'nodes-overview', title: 'Nodes Overview' },
      { id: 'creating-strategies', title: 'Creating Strategies' },
      { id: 'templates', title: 'Strategy Templates' },
      { id: 'node-connections', title: 'Node Connections' },
      { id: 'saving-loading', title: 'Saving & Loading' }
    ]
  },
  {
    id: 'ai-agents',
    title: 'AI Agents',
    icon: <MemoryIcon />,
    subsections: [
      { id: 'technical-analyst', title: 'Technical Analyst' },
      { id: 'fundamental-analyst', title: 'Fundamental Analyst' },
      { id: 'risk-manager', title: 'Risk Manager' },
      { id: 'trade-historian', title: 'Trade Historian' },
      { id: 'report-synthesizer', title: 'Report Synthesizer' },
      { id: 'execution-agent', title: 'Execution Agent' }
    ]
  },
  {
    id: 'broker-integrations',
    title: 'Broker Integrations',
    icon: <AccountBalanceIcon />,
    subsections: [
      { id: 'alpaca', title: 'Alpaca' },
      { id: 'binance', title: 'Binance' },
      { id: 'kucoin', title: 'KuCoin' },
      { id: 'coinbase', title: 'Coinbase Pro' },
      { id: 'kraken', title: 'Kraken' },
      { id: 'ibkr', title: 'Interactive Brokers' }
    ]
  },
  {
    id: 'api-webhook',
    title: 'API & Webhooks',
    icon: <ApiIcon />,
    subsections: [
      { id: 'openai-integration', title: 'OpenAI Integration' },
      { id: 'tradingview-webhooks', title: 'TradingView Webhooks' },
      { id: 'custom-webhooks', title: 'Custom Webhooks' },
      { id: 'api-documentation', title: 'API Documentation' }
    ]
  },
  {
    id: 'configuration',
    title: 'Configuration',
    icon: <SecurityIcon />,
    subsections: [
      { id: 'system-settings', title: 'System Settings' },
      { id: 'api-keys', title: 'API Keys Management' },
      { id: 'risk-settings', title: 'Risk Settings' },
      { id: 'notifications', title: 'Notifications' }
    ]
  },
  {
    id: 'guides-tutorials',
    title: 'Guides & Tutorials',
    icon: <ShowChartIcon />,
    subsections: [
      { id: 'technical-strategies', title: 'Technical Strategies' },
      { id: 'fundamental-strategies', title: 'Fundamental Strategies' },
      { id: 'hybrid-strategies', title: 'Hybrid Strategies' },
      { id: 'backtesting', title: 'Backtesting Guide' },
      { id: 'optimization', title: 'Strategy Optimization' }
    ]
  },
  {
    id: 'developers',
    title: 'Developers',
    icon: <IntegrationInstructionsIcon />,
    subsections: [
      { id: 'architecture', title: 'System Architecture' },
      { id: 'extending', title: 'Extending the Platform' },
      { id: 'custom-nodes', title: 'Creating Custom Nodes' },
      { id: 'contributing', title: 'Contributing Guidelines' }
    ]
  },
  {
    id: 'faqs',
    title: 'FAQs & Troubleshooting',
    icon: <HelpOutlineIcon />,
    subsections: [
      { id: 'common-issues', title: 'Common Issues' },
      { id: 'performance', title: 'Performance Optimization' },
      { id: 'security-best-practices', title: 'Security Best Practices' },
      { id: 'support', title: 'Getting Support' }
    ]
  }
];

// Content for each section
const docContent = {
  'key-concepts': {
    title: 'Key Concepts',
    content: `
      <h2>Key Concepts in AI Trading System</h2>
      <p>Understanding these fundamental concepts will help you get the most out of the AI Trading System platform.</p>
      
      <h3>Node-Based Strategy Building</h3>
      <p>The AI Trading System uses a visual node-based approach to strategy creation, where:</p>
      <ul>
        <li><strong>Nodes</strong> represent individual components with specific functionality (data sources, analysis tools, execution engines, etc.)</li>
        <li><strong>Edges</strong> connect nodes and represent the flow of data or signals between them</li>
        <li><strong>Node parameters</strong> allow you to customize the behavior of each node</li>
      </ul>
      <p>This approach allows you to build complex strategies without writing code.</p>
      
      <h3>AI-Assisted Trading</h3>
      <p>The platform leverages OpenAI's capabilities to:</p>
      <ul>
        <li><strong>Analyze market data</strong> using technical and fundamental approaches</li>
        <li><strong>Generate trading signals</strong> based on pattern recognition</li>
        <li><strong>Evaluate risk</strong> for each potential trade</li>
        <li><strong>Provide explanations</strong> for trading decisions</li>
      </ul>
      <p>Each AI component uses a dedicated OpenAI Assistant configured for its specific task.</p>
      
      <h3>Multi-Broker Architecture</h3>
      <p>The system supports multiple brokers simultaneously, allowing you to:</p>
      <ul>
        <li>Trade across different asset classes (stocks, crypto, etc.)</li>
        <li>Execute the same strategy on multiple venues</li>
        <li>Optimize for execution quality by broker</li>
        <li>Maintain separate risk parameters for each broker</li>
      </ul>
      
      <h3>Separation of Concerns</h3>
      <p>The platform separates different aspects of trading:</p>
      <ul>
        <li><strong>Data Collection</strong>: Gathering market data, news, and information</li>
        <li><strong>Analysis</strong>: Interpreting data to find patterns and opportunities</li>
        <li><strong>Signal Generation</strong>: Creating specific buy/sell recommendations</li>
        <li><strong>Risk Management</strong>: Determining position size and risk controls</li>
        <li><strong>Execution</strong>: Placing and managing orders with brokers</li>
        <li><strong>Reporting</strong>: Tracking performance and communicating results</li>
      </ul>
      <p>This modular approach allows for better testing, maintenance, and customization.</p>
    `
  },
  'technical-analysis': {
    title: 'Technical Analysis',
    content: `
      <h2>Technical Analysis</h2>
      <p>The Technical Analysis module provides comprehensive tools for analyzing price action, identifying patterns, and generating trading signals based on technical indicators.</p>
      
      <h3>Features</h3>
      
      <h4>Interactive Charting</h4>
      <ul>
        <li><strong>Multiple Timeframes</strong>: Analyze price data from 1-minute to monthly charts</li>
        <li><strong>Drawing Tools</strong>: Trend lines, Fibonacci retracements, channels, and more</li>
        <li><strong>Chart Types</strong>: Candlestick, bar, line, Heikin-Ashi, and Renko charts</li>
        <li><strong>Multi-chart View</strong>: Compare multiple symbols or timeframes simultaneously</li>
      </ul>
      
      <h4>Technical Indicators</h4>
      <ul>
        <li><strong>Trend Indicators</strong>: Moving averages, MACD, ADX, Parabolic SAR</li>
        <li><strong>Momentum Indicators</strong>: RSI, Stochastic, CCI, Williams %R</li>
        <li><strong>Volume Indicators</strong>: On-Balance Volume, Money Flow Index, Volume Profile</li>
        <li><strong>Volatility Indicators</strong>: Bollinger Bands, ATR, Standard Deviation</li>
        <li><strong>Custom Indicators</strong>: Create and save your own technical indicators</li>
      </ul>
      
      <h4>Pattern Recognition</h4>
      <ul>
        <li><strong>Candlestick Patterns</strong>: Automated detection of key patterns (engulfing, doji, hammer, etc.)</li>
        <li><strong>Chart Patterns</strong>: Identification of head and shoulders, triangles, flags, etc.</li>
        <li><strong>Support/Resistance</strong>: Dynamic calculation of key price levels</li>
        <li><strong>Divergence Scanner</strong>: Identify divergences between price and indicators</li>
      </ul>
      
      <h4>AI-Powered Analysis</h4>
      <ul>
        <li><strong>Technical Analyst AI</strong>: OpenAI-powered analysis of chart patterns and setups</li>
        <li><strong>Pattern Probability</strong>: AI-calculated success rates for identified patterns</li>
        <li><strong>Natural Language Querying</strong>: Ask questions about technical conditions in plain English</li>
        <li><strong>Signal Explanation</strong>: Get detailed explanations for trading signals</li>
      </ul>
      
      <h3>Integration with Strategy Builder</h3>
      <p>Technical Analysis can be directly integrated with the Strategy Builder through the Technical Analyst node:</p>
      <ul>
        <li>Configure which indicators and patterns to monitor</li>
        <li>Set signal generation criteria based on technical conditions</li>
        <li>Connect signals to other strategy components for execution</li>
      </ul>
      
      <h3>Backtesting</h3>
      <p>Test technical strategies against historical data to evaluate performance:</p>
      <ul>
        <li>Select date ranges for testing</li>
        <li>Adjust indicator parameters</li>
        <li>View detailed performance metrics</li>
        <li>Optimize parameters for best results</li>
      </ul>
    `
  },
  'risk-management': {
    title: 'Risk Management',
    content: `
      <h2>Risk Management</h2>
      <p>The Risk Management module provides tools to protect your capital and optimize position sizing for all trading activities.</p>
      
      <h3>Position Sizing</h3>
      
      <h4>Sizing Methods</h4>
      <ul>
        <li><strong>Fixed Risk</strong>: Risk a set percentage of account equity per trade</li>
        <li><strong>Fixed Position</strong>: Trade fixed position sizes regardless of volatility</li>
        <li><strong>Volatility-Based</strong>: Adjust position size based on market volatility (ATR)</li>
        <li><strong>Kelly Criterion</strong>: Optimize position sizes based on expected win rate and risk/reward ratio</li>
      </ul>
      
      <h4>Maximum Exposure Rules</h4>
      <ul>
        <li><strong>Per Position</strong>: Limit maximum size for any single position</li>
        <li><strong>Per Asset</strong>: Limit exposure to any single asset across strategies</li>
        <li><strong>Per Sector</strong>: Limit aggregate exposure to related assets</li>
        <li><strong>Total Portfolio</strong>: Cap overall leverage or market exposure</li>
      </ul>
      
      <h3>Stop Loss Management</h3>
      <ul>
        <li><strong>Fixed Stop Loss</strong>: Set stops at a specific price or percentage from entry</li>
        <li><strong>Technical Stop Loss</strong>: Place stops based on support/resistance levels or indicators</li>
        <li><strong>Volatility-Based Stops</strong>: Dynamic stops calculated using ATR or standard deviation</li>
        <li><strong>Trailing Stops</strong>: Automatically adjust stops to lock in profits as price moves favorably</li>
      </ul>
      
      <h3>Profit Taking</h3>
      <ul>
        <li><strong>Fixed Take Profit</strong>: Exit at predetermined price targets</li>
        <li><strong>Partial Profit Taking</strong>: Scale out of positions at multiple price levels</li>
        <li><strong>Indicator-Based Exits</strong>: Exit when technical indicators signal reversal</li>
        <li><strong>Time-Based Exits</strong>: Close positions after a specific duration</li>
      </ul>
      
      <h3>AI-Powered Risk Assessment</h3>
      <ul>
        <li><strong>Risk Manager AI</strong>: OpenAI-powered analysis of risk factors for each trade</li>
        <li><strong>Risk Scoring</strong>: AI-generated risk score for potential trades</li>
        <li><strong>Correlation Analysis</strong>: Identify hidden correlations between positions</li>
        <li><strong>Market Regime Detection</strong>: Adjust risk parameters based on current market conditions</li>
      </ul>
      
      <h3>Integration with Strategy Builder</h3>
      <p>Risk Management integrates with the Strategy Builder through the Risk Manager node:</p>
      <ul>
        <li>Configure risk parameters for each strategy</li>
        <li>Connect signal nodes to risk manager for position sizing</li>
        <li>Set strategy-specific stop loss rules</li>
        <li>Define profit taking methodologies</li>
      </ul>
      
      <h3>Risk Reporting</h3>
      <ul>
        <li><strong>Real-time Exposure</strong>: Current risk levels across all positions</li>
        <li><strong>VaR Analysis</strong>: Value at Risk calculations for portfolio</li>
        <li><strong>Drawdown Monitoring</strong>: Track and alert on drawdown thresholds</li>
        <li><strong>Stress Testing</strong>: Simulate portfolio performance under extreme market conditions</li>
      </ul>
    `
  },
  'execution': {
    title: 'Execution',
    content: `
      <h2>Execution</h2>
      <p>The Execution module handles the actual placement and management of orders with connected brokers, ensuring strategies are executed efficiently and reliably.</p>
      
      <h3>Order Types</h3>
      
      <h4>Basic Order Types</h4>
      <ul>
        <li><strong>Market Orders</strong>: Immediate execution at current market price</li>
        <li><strong>Limit Orders</strong>: Execution at specified price or better</li>
        <li><strong>Stop Orders</strong>: Convert to market order when price reaches trigger level</li>
        <li><strong>Stop-Limit Orders</strong>: Convert to limit order when price reaches trigger level</li>
      </ul>
      
      <h4>Advanced Order Types</h4>
      <ul>
        <li><strong>Trailing Stop Orders</strong>: Dynamic stops that adjust with favorable price movement</li>
        <li><strong>OCO (One-Cancels-Other)</strong>: Pair of orders where execution of one cancels the other</li>
        <li><strong>Iceberg Orders</strong>: Large orders divided into smaller visible portions to minimize market impact</li>
        <li><strong>TWAP/VWAP Orders</strong>: Time or volume-weighted average price execution strategies</li>
      </ul>
      
      <h3>Execution Modes</h3>
      <ul>
        <li><strong>Fully Automated</strong>: Orders placed without human intervention</li>
        <li><strong>Semi-Automated</strong>: Requires manual confirmation before order placement</li>
        <li><strong>Manual with Suggestions</strong>: Provides trade recommendations for manual execution</li>
        <li><strong>Paper Trading</strong>: Simulated execution without real money</li>
      </ul>
      
      <h3>Multi-Broker Support</h3>
      <p>The Execution module can route orders to various brokers based on:</p>
      <ul>
        <li><strong>Asset Availability</strong>: Automatically select broker that offers the asset</li>
        <li><strong>Cost Optimization</strong>: Route to broker with lowest fees or best execution</li>
        <li><strong>Account Preferences</strong>: Use specific accounts for different strategies</li>
        <li><strong>Smart Routing</strong>: Split orders across multiple brokers for large positions</li>
      </ul>
      
      <h3>Trade Parameters</h3>
      <ul>
        <li><strong>Risk Level</strong>: Determine the percentage of account to risk per trade</li>
        <li><strong>Maximum Trade Amount</strong>: Cap on position size regardless of signals</li>
        <li><strong>Default Trading Pair</strong>: Default instrument to trade when not specified</li>
        <li><strong>Trade Interval</strong>: Frequency of trade execution for systematic strategies</li>
      </ul>
      
      <h3>AI-Powered Execution</h3>
      <ul>
        <li><strong>Execution Agent AI</strong>: OpenAI-powered optimization of execution timing and method</li>
        <li><strong>Slippage Reduction</strong>: AI algorithms to minimize execution costs</li>
        <li><strong>Market Impact Analysis</strong>: Predictive assessment of order impact on price</li>
        <li><strong>Execution Quality Reports</strong>: AI-generated analysis of execution performance</li>
      </ul>
      
      <h3>Integration with Strategy Builder</h3>
      <p>Execution integrates with the Strategy Builder through the Execution Layer node:</p>
      <ul>
        <li>Configure execution parameters specific to each strategy</li>
        <li>Connect risk management output to execution decisions</li>
        <li>Set broker preferences and order types</li>
        <li>Define confirmation requirements and automation level</li>
      </ul>
      
      <h3>Order Monitoring and Management</h3>
      <ul>
        <li><strong>Order Status Tracking</strong>: Real-time updates on all pending and completed orders</li>
        <li><strong>Position Management</strong>: View and manage all open positions</li>
        <li><strong>Order Modification</strong>: Update pending orders with new parameters</li>
        <li><strong>Emergency Controls</strong>: Quickly flatten all positions or cancel all pending orders</li>
      </ul>
    `
  },
  'system-settings': {
    title: 'System Settings',
    content: `
      <h2>System Settings</h2>
      <p>The System Settings section allows you to configure all aspects of the AI Trading System platform to meet your specific requirements.</p>
      
      <h3>API Keys Management</h3>
      
      <h4>Broker API Keys</h4>
      <p>Configure credentials for various brokers and exchanges:</p>
      <ul>
        <li><strong>Alpaca</strong>: API Key and Secret Key</li>
        <li><strong>Binance</strong>: API Key and Secret Key</li>
        <li><strong>KuCoin</strong>: API Key, Secret Key, and Passphrase</li>
        <li><strong>Coinbase Pro</strong>: API Key, Secret Key, and Passphrase</li>
        <li><strong>Kraken</strong>: API Key and Private Key</li>
        <li><strong>Interactive Brokers</strong>: Account ID and API Connection Setup</li>
      </ul>
      <p>Each broker configuration includes a Test Connection button to verify credentials.</p>
      
      <h4>AI Service Keys</h4>
      <ul>
        <li><strong>OpenAI API Key</strong>: For accessing GPT models and assistants</li>
        <li><strong>Assistant IDs</strong>: Configuration for each AI agent node</li>
      </ul>
      
      <h3>Webhook Configurations</h3>
      <ul>
        <li><strong>OpenAI Webhooks</strong>: Setup for assistant-based notifications</li>
        <li><strong>TradingView Webhooks</strong>: Integration with TradingView alerts</li>
        <li><strong>Custom Webhooks</strong>: Configuration for third-party service integration</li>
      </ul>
      
      <h3>Notification Settings</h3>
      <ul>
        <li><strong>Email Notifications</strong>: Configuration for trade alerts, system updates, etc.</li>
        <li><strong>Telegram Integration</strong>: Bot token and chat ID for Telegram reporting</li>
        <li><strong>SMS Alerts</strong>: Phone number and preferences for critical alerts</li>
        <li><strong>In-App Notifications</strong>: Priority settings for application notifications</li>
      </ul>
      
      <h3>Data Sources</h3>
      <ul>
        <li><strong>Market Data Providers</strong>: Configure data feeds for various asset classes</li>
        <li><strong>News Sources</strong>: API keys for financial news services</li>
        <li><strong>Economic Calendar</strong>: Settings for economic data releases and events</li>
        <li><strong>Alternative Data</strong>: Configuration for non-traditional data sources</li>
      </ul>
      
      <h3>System Preferences</h3>
      <ul>
        <li><strong>User Interface</strong>: Theme selection, layout preferences, display options</li>
        <li><strong>Time Zone</strong>: Set local time zone for system operations</li>
        <li><strong>Date Format</strong>: Configure date and time display format</li>
        <li><strong>Currency Display</strong>: Set base currency and display preferences</li>
      </ul>
      
      <h3>Backup and Storage</h3>
      <ul>
        <li><strong>Strategy Backup</strong>: Automated backup frequency and location</li>
        <li><strong>Trade History Export</strong>: Settings for periodic data exports</li>
        <li><strong>Log Retention</strong>: Configure how long system logs are kept</li>
        <li><strong>Cloud Sync</strong>: Settings for syncing data across multiple devices</li>
      </ul>
      
      <h3>Access Control</h3>
      <ul>
        <li><strong>User Management</strong>: Add, remove, or modify user accounts</li>
        <li><strong>Permission Levels</strong>: Configure access rights for different users</li>
        <li><strong>Authentication Settings</strong>: Two-factor authentication, session timeouts</li>
        <li><strong>API Access</strong>: Manage external API access to the system</li>
      </ul>
    `
  },
  'trade-history': {
    title: 'Trade History',
    content: `
      <h2>Trade History</h2>
      <p>The Trade History module provides comprehensive tracking, analysis, and reporting of all your trading activities.</p>
      
      <h3>Trade Logging</h3>
      
      <h4>Trade Details</h4>
      <ul>
        <li><strong>Basic Information</strong>: Symbol, side (buy/sell), price, quantity, timestamp</li>
        <li><strong>Order Details</strong>: Order type, execution method, fees, slippage</li>
        <li><strong>Strategy Context</strong>: Which strategy initiated the trade, signal source</li>
        <li><strong>Risk Parameters</strong>: Stop loss, take profit, risk amount, R-multiple</li>
      </ul>
      
      <h4>Multi-Broker Syncing</h4>
      <ul>
        <li>Automatic synchronization of trades from all connected brokers</li>
        <li>Manual trade entry for external or non-connected accounts</li>
        <li>Reconciliation tools to ensure all trades are properly recorded</li>
      </ul>
      
      <h3>Performance Analytics</h3>
      
      <h4>Trade Statistics</h4>
      <ul>
        <li><strong>Profitability Metrics</strong>: Win rate, average win/loss, expectancy, Sharpe ratio</li>
        <li><strong>Time Analysis</strong>: Performance by day of week, time of day, month</li>
        <li><strong>Instrument Analysis</strong>: Performance by symbol, asset class, sector</li>
        <li><strong>Strategy Analysis</strong>: Performance breakdown by trading strategy</li>
      </ul>
      
      <h4>Detailed Visualizations</h4>
      <ul>
        <li><strong>Equity Curve</strong>: Visual representation of account growth over time</li>
        <li><strong>Drawdown Chart</strong>: Historical drawdowns with duration metrics</li>
        <li><strong>Profit Distribution</strong>: Histogram of trade P&L outcomes</li>
        <li><strong>Heat Maps</strong>: Visual patterns of trading performance</li>
      </ul>
      
      <h3>Trade Review Tools</h3>
      
      <h4>Individual Trade Analysis</h4>
      <ul>
        <li><strong>Trade Replay</strong>: Visualize how the trade played out on a chart</li>
        <li><strong>Market Context</strong>: News, events, and market conditions during the trade</li>
        <li><strong>Signal Quality</strong>: Assessment of the original trading signal</li>
        <li><strong>Execution Quality</strong>: Analysis of entry/exit timing and slippage</li>
      </ul>
      
      <h4>Journaling Features</h4>
      <ul>
        <li><strong>Trade Notes</strong>: Add subjective notes and observations for each trade</li>
        <li><strong>Screenshots</strong>: Attach chart images and other visual records</li>
        <li><strong>Tags</strong>: Categorize trades with custom tags for better analysis</li>
        <li><strong>Emotional State</strong>: Track psychological factors affecting trading decisions</li>
      </ul>
      
      <h3>AI-Powered Insights</h3>
      <ul>
        <li><strong>Trade Historian AI</strong>: OpenAI-powered analysis of trading patterns and biases</li>
        <li><strong>Performance Recommendations</strong>: AI-generated suggestions for improvement</li>
        <li><strong>Pattern Recognition</strong>: Identification of recurring issues or strengths</li>
        <li><strong>Natural Language Querying</strong>: Ask questions about trading history in plain English</li>
      </ul>
      
      <h3>Reporting</h3>
      <ul>
        <li><strong>Periodic Reports</strong>: Daily, weekly, monthly performance summaries</li>
        <li><strong>Tax Documents</strong>: Export trade data for tax reporting purposes</li>
        <li><strong>Custom Reports</strong>: Generate reports with user-selected metrics and timeframes</li>
        <li><strong>Report Sharing</strong>: Securely share reports with team members or accountants</li>
      </ul>
    `
  },
  'fundamental-analysis': {
    title: 'Fundamental Analysis',
    content: `
      <h2>Fundamental Analysis</h2>
      <p>The Fundamental Analysis module provides tools and data for evaluating investments based on economic, financial, and qualitative factors that affect asset value.</p>
      
      <h3>Data Sources</h3>
      
      <h4>Financial Data</h4>
      <ul>
        <li><strong>Company Financials</strong>: Income statements, balance sheets, cash flow statements</li>
        <li><strong>Financial Ratios</strong>: P/E ratio, EPS, ROE, debt-to-equity, current ratio, etc.</li>
        <li><strong>Earnings Reports</strong>: Historical and upcoming earnings announcements</li>
        <li><strong>Dividend Information</strong>: Yield, payment history, ex-dividend dates</li>
      </ul>
      
      <h4>Economic Indicators</h4>
      <ul>
        <li><strong>Macroeconomic Data</strong>: GDP, inflation, employment, interest rates</li>
        <li><strong>Central Bank Announcements</strong>: Federal Reserve, ECB, and other central bank statements</li>
        <li><strong>Sector Performance</strong>: Industry trends and comparative analysis</li>
        <li><strong>Global Events</strong>: Geopolitical developments affecting markets</li>
      </ul>
      
      <h4>News and Sentiment</h4>
      <ul>
        <li><strong>Financial News</strong>: Real-time aggregation from major financial news sources</li>
        <li><strong>Social Media Sentiment</strong>: Analysis of market sentiment from Twitter, Reddit, etc.</li>
        <li><strong>Analyst Ratings</strong>: Consensus estimates, price targets, and recommendations</li>
        <li><strong>Insider Trading</strong>: Tracking of institutional and insider transactions</li>
      </ul>
      
      <h4>Cryptocurrency Fundamentals</h4>
      <ul>
        <li><strong>On-chain Metrics</strong>: Network activity, wallet distribution, miner data</li>
        <li><strong>DeFi Analytics</strong>: TVL, protocol revenue, yield opportunities</li>
        <li><strong>Token Economics</strong>: Supply dynamics, inflation rate, tokenomics</li>
        <li><strong>Project Development</strong>: GitHub activity, roadmap progress, team updates</li>
      </ul>
      
      <h3>AI-Powered Fundamental Analysis</h3>
      <ul>
        <li><strong>Fundamental Analyst AI</strong>: OpenAI-powered analysis of financial data and news</li>
        <li><strong>News Summarization</strong>: AI-generated summaries of important news events</li>
        <li><strong>Valuation Models</strong>: Automated DCF, comparables, and other valuation methodologies</li>
        <li><strong>Natural Language Querying</strong>: Ask questions about fundamental data in plain English</li>
      </ul>
      
      <h3>Integration with Strategy Builder</h3>
      <p>Fundamental Analysis integrates with the Strategy Builder through the Fundamental Analyst node:</p>
      <ul>
        <li>Configure which fundamental factors to monitor</li>
        <li>Set thresholds for generating signals based on fundamental data</li>
        <li>Combine fundamental insights with technical signals</li>
      </ul>
      
      <h3>Visualization Tools</h3>
      <ul>
        <li><strong>Financial Charts</strong>: Visual representation of key financial metrics over time</li>
        <li><strong>Comparative Analysis</strong>: Side-by-side comparison of multiple companies</li>
        <li><strong>Heatmaps</strong>: Sector and industry performance visualization</li>
        <li><strong>Correlation Matrices</strong>: Identify relationships between assets and economic factors</li>
      </ul>
    `
  },
  'dashboard': {
    title: 'Dashboard Overview',
    content: `
      <h2>Dashboard</h2>
      <p>The Dashboard provides a complete overview of your trading activities, performance metrics, and system status in one centralized view.</p>
      
      <h3>Main Components</h3>
      
      <h4>Performance Summary</h4>
      <ul>
        <li><strong>Portfolio Value</strong>: Current total value across all connected brokers</li>
        <li><strong>Daily P&L</strong>: Profit and loss for the current trading day</li>
        <li><strong>Performance Graph</strong>: Visual representation of account growth over time</li>
        <li><strong>Win/Loss Ratio</strong>: Proportion of profitable trades to losing trades</li>
      </ul>
      
      <h4>Active Strategies</h4>
      <ul>
        <li>Status indicators showing which strategies are currently running</li>
        <li>Performance metrics for each active strategy</li>
        <li>Quick actions to start, stop, or edit strategies</li>
      </ul>
      
      <h4>Recent Trades</h4>
      <ul>
        <li>List of most recent executions across all strategies</li>
        <li>Key details including symbol, size, price, timestamp, and P&L</li>
        <li>Visual indicators for trade type (buy/sell) and outcome (profit/loss)</li>
      </ul>
      
      <h4>System Status</h4>
      <ul>
        <li>Connection status for all integrated brokers and APIs</li>
        <li>Webhook status indicators</li>
        <li>System resource utilization</li>
      </ul>
      
      <h4>Market Overview</h4>
      <ul>
        <li>Current market hours status (open/closed)</li>
        <li>Major index performance</li>
        <li>Watchlist of key assets with current prices and daily change</li>
      </ul>
      
      <h3>Customization Options</h3>
      <p>The Dashboard can be customized to prioritize the information most relevant to your trading:</p>
      <ul>
        <li>Rearrange widgets via drag-and-drop</li>
        <li>Expand or collapse sections as needed</li>
        <li>Configure time periods for performance metrics</li>
        <li>Set custom watchlists for the Market Overview</li>
      </ul>
      
      <h3>Navigation</h3>
      <p>From the Dashboard, you can quickly navigate to other system components:</p>
      <ul>
        <li>Click on any strategy to open it in the Strategy Builder</li>
        <li>Select any trade to view full details in Trade History</li>
        <li>Access system settings from the status panel</li>
      </ul>
    `
  },
  'technical-strategies': {
    title: 'Technical Trading Strategies',
    content: `
      <h2>Technical Trading Strategies</h2>
      <p>Learn how to build effective technical analysis strategies with the AI Trading System platform.</p>
      
      <h3>Moving Average Crossover Strategy</h3>
      <p>A popular strategy based on the crossover of two moving averages of different periods.</p>
      <ol>
        <li><strong>Setup:</strong> Use a Technical Analyst node with parameters for MA(50) and MA(200)</li>
        <li><strong>Signal Logic:</strong> Generate a buy signal when MA(50) crosses above MA(200), and a sell signal when it crosses below</li>
        <li><strong>Risk Management:</strong> Set stop-loss at recent swing low for buy orders, or swing high for sell orders</li>
        <li><strong>Position Sizing:</strong> Risk no more than 1-2% of account per trade</li>
      </ol>
      
      <h3>RSI Overbought/Oversold Strategy</h3>
      <p>A mean-reversion strategy using the Relative Strength Index (RSI) indicator.</p>
      <ol>
        <li><strong>Setup:</strong> Configure a Technical Analyst node to monitor RSI(14)</li>
        <li><strong>Signal Logic:</strong> Generate buy signals when RSI drops below 30 and begins to rise, sell signals when RSI rises above 70 and begins to fall</li>
        <li><strong>Confirmation:</strong> Add a second Technical Analyst node to confirm trend direction</li>
        <li><strong>Execution:</strong> Set limit orders to capture best entry prices</li>
      </ol>
      
      <h3>MACD Momentum Strategy</h3>
      <p>A trend-following strategy using the Moving Average Convergence Divergence (MACD) indicator.</p>
      <ol>
        <li><strong>Setup:</strong> Configure a Technical Analyst node to calculate MACD with standard parameters (12, 26, 9)</li>
        <li><strong>Signal Logic:</strong> Generate buy signals when MACD line crosses above signal line in a positive direction, sell signals when it crosses below</li>
        <li><strong>Filter:</strong> Only take trades in the direction of the overall trend (use a longer-term moving average to determine)</li>
        <li><strong>Exit Strategy:</strong> Exit when MACD crosses back in the opposite direction or when a predetermined profit target is reached</li>
      </ol>
      
      <h3>Bollinger Band Squeeze Strategy</h3>
      <p>A volatility breakout strategy using Bollinger Bands and Keltner Channels.</p>
      <ol>
        <li><strong>Setup:</strong> Use a Technical Analyst node to identify when Bollinger Bands contract inside Keltner Channels (indicating low volatility)</li>
        <li><strong>Signal Logic:</strong> Wait for a breakout from the squeeze, then enter in the direction of the breakout</li>
        <li><strong>Confirmation:</strong> Add volume analysis to confirm the breakout is valid</li>
        <li><strong>Risk Management:</strong> Set stop-loss at the opposite side of the bands/channels</li>
      </ol>
    `
  },
  'quick-start': {
    title: 'Quick Start Guide',
    content: `
      <h2>Quick Start Guide</h2>
      <p>Get up and running with AI Trading System in under 10 minutes. This guide walks you through the basic setup and creating your first strategy.</p>
      
      <h3>Step 1: Configure API Keys</h3>
      <ol>
        <li>Navigate to the <strong>Settings</strong> page.</li>
        <li>Under the <strong>Brokers</strong> tab, select your preferred broker.</li>
        <li>Enter your API key and secret.</li>
        <li>Click <strong>Test Connection</strong> to verify.</li>
      </ol>
      
      <h3>Step 2: OpenAI Integration</h3>
      <ol>
        <li>Under the <strong>API Keys</strong> section, enter your OpenAI API key.</li>
        <li>Test the connection to ensure it works.</li>
      </ol>
      
      <h3>Step 3: Create Your First Strategy</h3>
      <ol>
        <li>Navigate to <strong>Strategy Builder</strong>.</li>
        <li>Click <strong>New Strategy</strong> or select a template.</li>
        <li>Drag and connect nodes to create your strategy flow.</li>
        <li>Configure each node by clicking on it and adjusting parameters.</li>
        <li>Save your strategy with a descriptive name.</li>
      </ol>
    `
  },
  'templates': {
    title: 'Strategy Templates',
    content: `
      <h2>Strategy Templates</h2>
      <p>The AI Trading System includes pre-built strategy templates to help you quickly implement common trading approaches without starting from scratch.</p>
      
      <h3>Available Templates</h3>
      
      <h4>Technical Strategy Templates</h4>
      <ul>
        <li><strong>Moving Average Crossover</strong>: Classic strategy using the crossover of two moving averages</li>
        <li><strong>RSI Mean Reversion</strong>: Strategy for trading overbought and oversold conditions</li>
        <li><strong>Bollinger Band Breakout</strong>: Volatility-based breakout strategy</li>
        <li><strong>Multi-Timeframe Momentum</strong>: Strategy combining momentum indicators across timeframes</li>
      </ul>
      
      <h4>Fundamental Strategy Templates</h4>
      <ul>
        <li><strong>Earnings Announcement</strong>: Strategy for trading around corporate earnings releases</li>
        <li><strong>Economic Data Release</strong>: Strategy for trading economic announcements</li>
        <li><strong>News Sentiment</strong>: Strategy based on news sentiment analysis</li>
      </ul>
      
      <h4>Hybrid Strategy Templates</h4>
      <ul>
        <li><strong>Technical + Fundamental</strong>: Combined approach using both analysis types</li>
        <li><strong>Multi-Asset Allocation</strong>: Portfolio allocation strategy across different asset classes</li>
        <li><strong>Sector Rotation</strong>: Strategy for rotating between market sectors</li>
      </ul>
      
      <h4>Reporting Templates</h4>
      <ul>
        <li><strong>Telegram Reporting</strong>: Strategy with integrated Telegram notifications and reports</li>
        <li><strong>Performance Dashboard</strong>: Strategy with detailed performance metrics and visualization</li>
        <li><strong>Email Summary</strong>: Strategy that sends periodic email reports</li>
      </ul>
      
      <h3>Using Templates</h3>
      <ol>
        <li><strong>Select a Template</strong>: From the Strategy Builder, click "New Strategy" and choose from available templates</li>
        <li><strong>Customize Parameters</strong>: Adjust the parameters of each node to match your preferences</li>
        <li><strong>Configure Broker Integration</strong>: Connect the strategy to your preferred broker</li>
        <li><strong>Save and Deploy</strong>: Save your customized strategy with a unique name and deploy it</li>
      </ol>
      
      <h3>Customizing Templates</h3>
      <p>All templates can be modified to suit your specific requirements:</p>
      <ul>
        <li><strong>Add/Remove Nodes</strong>: Expand or simplify the strategy as needed</li>
        <li><strong>Change Indicators</strong>: Substitute different technical indicators</li>
        <li><strong>Adjust Risk Parameters</strong>: Modify position sizing and risk management rules</li>
        <li><strong>Add Custom Logic</strong>: Integrate additional conditions or filters</li>
      </ul>
      
      <h3>Saving Custom Templates</h3>
      <p>Once you've created a strategy you'd like to reuse:</p>
      <ol>
        <li>Click "Save as Template" in the Strategy Builder</li>
        <li>Give your template a descriptive name</li>
        <li>Add documentation to explain the strategy's approach</li>
        <li>Your template will now appear in the templates list for future use</li>
      </ol>
    `
  },
  'technical-analyst': {
    title: 'Technical Analyst Node',
    content: `
      <h2>Technical Analyst Node</h2>
      <p>The Technical Analyst node is a core component of the AI Trading System that uses OpenAI's capabilities to analyze charts, indicators, and market data.</p>
      
      <h3>Configuration</h3>
      
      <h4>Basic Settings</h4>
      <ul>
        <li><strong>Symbol</strong>: The trading pair or asset to analyze (e.g., BTC/USD, AAPL)</li>
        <li><strong>Timeframe</strong>: The chart timeframe to analyze (e.g., 1m, 5m, 15m, 1h, 4h, 1d)</li>
        <li><strong>Indicators</strong>: Technical indicators to include in the analysis</li>
        <li><strong>Assistant ID</strong>: OpenAI Assistant ID for this specific node</li>
      </ul>
      
      <h4>Advanced Settings</h4>
      <ul>
        <li><strong>Analysis Depth</strong>: How detailed the analysis should be</li>
        <li><strong>Update Frequency</strong>: How often to perform new analysis</li>
        <li><strong>Signal Threshold</strong>: Confidence level required to generate signals</li>
        <li><strong>Include Price Action</strong>: Whether to analyze candlestick patterns</li>
      </ul>
      
      <h3>OpenAI Integration</h3>
      <p>The Technical Analyst node leverages OpenAI's capabilities through:</p>
      <ul>
        <li><strong>Assistant Configuration</strong>: Each node uses a dedicated OpenAI Assistant with specific instructions</li>
        <li><strong>Test Connection</strong>: Verify that your OpenAI API key and Assistant ID work properly</li>
        <li><strong>Auto-Save</strong>: Successful connections are automatically saved</li>
      </ul>
      
      <h3>Input/Output</h3>
      
      <h4>Inputs</h4>
      <ul>
        <li><strong>Market Data</strong>: Price and volume data for the specified symbol</li>
        <li><strong>Custom Parameters</strong>: Additional parameters from connected nodes</li>
      </ul>
      
      <h4>Outputs</h4>
      <ul>
        <li><strong>Trading Signals</strong>: Buy, sell, or hold recommendations</li>
        <li><strong>Technical Analysis</strong>: Detailed breakdown of market conditions</li>
        <li><strong>Support/Resistance</strong>: Key price levels identified</li>
        <li><strong>Pattern Recognition</strong>: Identified chart patterns and formations</li>
      </ul>
      
      <h3>Example Configurations</h3>
      
      <h4>Trend Following Analyst</h4>
      <pre><code>{
  "symbol": "BTC/USD",
  "timeframe": "4h",
  "indicators": ["MA(50)", "MA(200)", "MACD", "ADX"],
  "analysisDepth": "detailed",
  "signalThreshold": "high"
}</code></pre>
      
      <h4>Swing Trading Analyst</h4>
      <pre><code>{
  "symbol": "ETH/USD",
  "timeframe": "1d",
  "indicators": ["RSI", "Bollinger Bands", "Stochastic"],
  "includePriceAction": true,
  "signalThreshold": "medium"
}</code></pre>
      
      <h3>Best Practices</h3>
      <ul>
        <li><strong>Create Specific Assistants</strong>: Configure OpenAI Assistants with clear, focused instructions for each analysis type</li>
        <li><strong>Match Timeframes</strong>: Ensure the timeframe matches your trading strategy horizon</li>
        <li><strong>Limit Indicators</strong>: Focus on a few relevant indicators rather than overwhelming with too many</li>
        <li><strong>Connect to Risk Management</strong>: Always feed Technical Analyst outputs to a Risk Manager node</li>
      </ul>
    `
  },
  'architecture': {
    title: 'System Architecture',
    content: `
      <h2>System Architecture</h2>
      <p>The AI Trading System follows a modular, event-driven architecture that enables flexibility, scalability, and extensibility.</p>
      
      <h3>High-Level Architecture</h3>
      
      <h4>Frontend Layer</h4>
      <ul>
        <li><strong>User Interface</strong>: React-based SPA with Material-UI components</li>
        <li><strong>Strategy Builder</strong>: Interactive node-based canvas using React Flow</li>
        <li><strong>Charts</strong>: Technical analysis charts using TradingView's lightweight charts</li>
        <li><strong>State Management</strong>: React context API for application state</li>
      </ul>
      
      <h4>Backend Services</h4>
      <ul>
        <li><strong>API Server</strong>: Express.js REST API for frontend communications</li>
        <li><strong>Strategy Engine</strong>: Node.js service that executes trading strategies</li>
        <li><strong>Data Service</strong>: Manages market data collection and processing</li>
        <li><strong>AI Integration Layer</strong>: Handles communication with OpenAI's API</li>
      </ul>
      
      <h4>Data Storage</h4>
      <ul>
        <li><strong>Database</strong>: MongoDB for strategy configurations, user settings, and trade history</li>
        <li><strong>Time-series DB</strong>: InfluxDB for market data and performance metrics</li>
        <li><strong>Cache Layer</strong>: Redis for high-performance data caching</li>
      </ul>
      
      <h4>Integration Layer</h4>
      <ul>
        <li><strong>Broker Connectors</strong>: Adapters for various brokers and exchanges</li>
        <li><strong>Webhook Handlers</strong>: Process incoming webhooks from TradingView and other services</li>
        <li><strong>Notification Service</strong>: Manages alerts via email, Telegram, and other channels</li>
      </ul>
      
      <h3>Data Flow</h3>
      
      <h4>Strategy Execution Flow</h4>
      <ol>
        <li>User creates/edits a strategy in the Strategy Builder</li>
        <li>Strategy configuration is saved to the database</li>
        <li>When started, the Strategy Engine loads the configuration</li>
        <li>Strategy Engine initializes all nodes and connections</li>
        <li>Data flows through the strategy graph from data sources to execution nodes</li>
        <li>AI nodes process data through OpenAI integrations</li>
        <li>Execution nodes interact with broker APIs to place trades</li>
        <li>Results are stored in the database and displayed in the UI</li>
      </ol>
      
      <h4>Event-Driven Communication</h4>
      <p>The system uses an event bus architecture where:</p>
      <ul>
        <li>Components publish events when their state changes</li>
        <li>Other components subscribe to relevant events</li>
        <li>This loose coupling allows for easier scaling and maintenance</li>
      </ul>
      
      <h3>Security Architecture</h3>
      <ul>
        <li><strong>API Authentication</strong>: JWT-based authentication for all API requests</li>
        <li><strong>Encryption</strong>: API keys and sensitive data encrypted at rest</li>
        <li><strong>Access Control</strong>: Role-based permissions system</li>
        <li><strong>Audit Logging</strong>: All system actions are logged for security review</li>
      </ul>
      
      <h3>Deployment Architecture</h3>
      
      <h4>Containerization</h4>
      <ul>
        <li>All services are containerized using Docker</li>
        <li>Docker Compose for development environments</li>
        <li>Kubernetes for production deployments</li>
      </ul>
      
      <h4>Scalability</h4>
      <ul>
        <li>Horizontal scaling for all stateless services</li>
        <li>Database sharding for large data volumes</li>
        <li>Load balancing across service instances</li>
      </ul>
    `
  },
  'custom-nodes': {
    title: 'Creating Custom Nodes',
    content: `
      <h2>Creating Custom Nodes</h2>
      <p>The AI Trading System allows advanced users to create custom nodes that extend the platform's capabilities.</p>
      
      <h3>Node Development Process</h3>
      
      <h4>Planning Your Node</h4>
      <ol>
        <li><strong>Define Purpose</strong>: Determine what your node will do</li>
        <li><strong>Identify Inputs/Outputs</strong>: What data will it consume and produce?</li>
        <li><strong>Design Parameters</strong>: What settings will users need to configure?</li>
        <li><strong>Plan UI</strong>: How will your node appear in the Strategy Builder?</li>
      </ol>
      
      <h4>Creating Node Components</h4>
      <p>Each custom node requires several components:</p>
      <ul>
        <li><strong>Node Definition</strong>: JSON schema defining the node's properties</li>
        <li><strong>Node Component</strong>: React component for the node's appearance</li>
        <li><strong>Node Logic</strong>: JavaScript module containing business logic</li>
        <li><strong>Settings Panel</strong>: Component for configuring node parameters</li>
      </ul>
      
      <h3>Node Definition Example</h3>
      <pre><code>// myCustomNode.js
export const myCustomNodeDefinition = {
  type: 'myCustomNode',
  label: 'My Custom Node',
  description: 'A custom node that does something interesting',
  category: 'custom',
  inputs: [
    { id: 'dataIn', label: 'Data Input' }
  ],
  outputs: [
    { id: 'dataOut', label: 'Processed Data' }
  ],
  defaultParams: {
    threshold: 0.5,
    enableFeatureX: true
  },
  icon: CustomNodeIcon
};</code></pre>
      
      <h3>Node Logic Implementation</h3>
      <pre><code>// myCustomNodeLogic.js
export function processData(inputs, params) {
  const { dataIn } = inputs;
  const { threshold, enableFeatureX } = params;
  
  // Your custom logic here
  let processedData = dataIn.map(item => {
    // Transform data according to your algorithm
    return {
      ...item,
      signal: item.value > threshold ? 'BUY' : 'SELL'
    };
  });
  
  if (enableFeatureX) {
    // Additional processing for feature X
    processedData = applyFeatureX(processedData);
  }
  
  return {
    dataOut: processedData
  };
}</code></pre>
      
      <h3>Node UI Component</h3>
      <pre><code>// MyCustomNodeComponent.jsx
import React from 'react';
import { Handle } from 'react-flow-renderer';

function MyCustomNodeComponent({ data }) {
  return (
    <div className="custom-node">
      <Handle
        type="target"
        position="left"
        id="dataIn"
        style={{ background: '#555' }}
      />
      <div>
        <div className="custom-node-header">
          <span>{data.label || 'My Custom Node'}</span>
        </div>
        <div className="custom-node-content">
          <p>Threshold: {data.params.threshold}</p>
          <p>Feature X: {data.params.enableFeatureX ? 'Enabled' : 'Disabled'}</p>
        </div>
      </div>
      <Handle
        type="source"
        position="right"
        id="dataOut"
        style={{ background: '#555' }}
      />
    </div>
  );
}

export default MyCustomNodeComponent;</code></pre>
      
      <h3>Registering Your Custom Node</h3>
      <p>Once you've created all the necessary components, register your node with the system:</p>
      <pre><code>// In your node registry file
import { myCustomNodeDefinition } from './myCustomNode';
import MyCustomNodeComponent from './MyCustomNodeComponent';
import { processData } from './myCustomNodeLogic';

function registerCustomNodes(nodeRegistry) {
  nodeRegistry.registerNode({
    definition: myCustomNodeDefinition,
    component: MyCustomNodeComponent,
    logic: processData
  });
}</code></pre>
      
      <h3>Node Testing</h3>
      <p>Before deploying your custom node:</p>
      <ol>
        <li>Create unit tests for your node logic</li>
        <li>Test the node in isolation with sample inputs</li>
        <li>Test the node in a complete strategy flow</li>
        <li>Verify the UI renders correctly</li>
      </ol>
    `
  },
  'common-issues': {
    title: 'Common Issues',
    content: `
      <h2>Common Issues and Troubleshooting</h2>
      <p>This section covers frequently encountered issues and their solutions.</p>
      
      <h3>API Connection Issues</h3>
      
      <h4>Broker Connection Failures</h4>
      <ul>
        <li><strong>Issue</strong>: Unable to connect to broker API</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Verify your API key and secret are entered correctly</li>
            <li>Check that your API key has trading permissions enabled</li>
            <li>Ensure your IP address isn't blocked by the broker</li>
            <li>Verify the broker's API servers are operational</li>
            <li>Check for API endpoint changes in broker documentation</li>
          </ul>
        </li>
      </ul>
      
      <h4>OpenAI API Issues</h4>
      <ul>
        <li><strong>Issue</strong>: OpenAI API calls failing or timing out</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Check your OpenAI API key is valid and has sufficient credits</li>
            <li>Verify the Assistant IDs exist in your OpenAI account</li>
            <li>Reduce the complexity of requests if hitting rate limits</li>
            <li>Check OpenAI's status page for service disruptions</li>
          </ul>
        </li>
      </ul>
      
      <h3>Strategy Execution Problems</h3>
      
      <h4>Strategies Not Starting</h4>
      <ul>
        <li><strong>Issue</strong>: Strategy won't start when activated</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Check for missing connections between nodes</li>
            <li>Verify all required node parameters are set</li>
            <li>Ensure broker connections are active</li>
            <li>Check system logs for specific error messages</li>
          </ul>
        </li>
      </ul>
      
      <h4>No Trading Signals Generated</h4>
      <ul>
        <li><strong>Issue</strong>: Strategy running but not producing signals</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Verify market data is being received</li>
            <li>Check signal thresholds aren't set too high</li>
            <li>Ensure technical indicators have enough data to calculate</li>
            <li>Check that the symbol/asset is available and active</li>
            <li>Verify OpenAI connections are working properly</li>
          </ul>
        </li>
      </ul>
      
      <h3>Performance Issues</h3>
      
      <h4>UI Responsiveness</h4>
      <ul>
        <li><strong>Issue</strong>: Strategy Builder becomes slow with many nodes</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Simplify complex strategies into sub-components</li>
            <li>Close unused tabs and windows</li>
            <li>Clear browser cache and reload</li>
            <li>Ensure your device meets minimum system requirements</li>
          </ul>
        </li>
      </ul>
      
      <h4>Memory Usage</h4>
      <ul>
        <li><strong>Issue</strong>: System consuming excessive memory</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Limit the number of concurrent running strategies</li>
            <li>Reduce the timeframes of data being processed</li>
            <li>Close and restart the application if running for extended periods</li>
            <li>Check for memory leaks in custom nodes</li>
          </ul>
        </li>
      </ul>
      
      <h3>Data Synchronization</h3>
      
      <h4>Missing Historical Data</h4>
      <ul>
        <li><strong>Issue</strong>: Historical data missing for backtesting</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Manually trigger data sync for the specific symbol and timeframe</li>
            <li>Check data provider API status and limits</li>
            <li>Verify the date range is available from your data source</li>
            <li>Import data from alternative sources if available</li>
          </ul>
        </li>
      </ul>
      
      <h4>Real-time Data Delays</h4>
      <ul>
        <li><strong>Issue</strong>: Real-time data feed experiencing delays</li>
        <li><strong>Solutions</strong>:
          <ul>
            <li>Check your internet connection stability</li>
            <li>Verify websocket connections are active</li>
            <li>Check if you've exceeded API rate limits</li>
            <li>Restart the data service component</li>
          </ul>
        </li>
      </ul>
      
      <h3>Getting Support</h3>
      <p>If you continue to experience issues after trying these solutions:</p>
      <ul>
        <li>Check the <a href="#">community forums</a> for similar issues and solutions</li>
        <li>Review detailed logs in the system's log directory</li>
        <li>Submit a support ticket with detailed information about your issue</li>
        <li>For enterprise users, contact your dedicated support representative</li>
      </ul>
    `
  },
  'api-documentation': {
    title: 'API Documentation',
    content: `
      <h2>API Documentation</h2>
      <p>The AI Trading System provides a comprehensive API that allows developers to integrate with the platform and extend its functionality.</p>
      
      <h3>API Overview</h3>
      <p>The system exposes RESTful API endpoints for:</p>
      <ul>
        <li><strong>Data Access</strong>: Retrieve market data, account information, and trading history</li>
        <li><strong>Strategy Management</strong>: Create, read, update, and delete trading strategies</li>
        <li><strong>Trade Execution</strong>: Place, modify, and cancel orders</li>
        <li><strong>Account Management</strong>: Configure integrations and system settings</li>
      </ul>
      
      <h3>Authentication</h3>
      <p>All API requests require authentication using:</p>
      <ul>
        <li><strong>API Key</strong>: Unique identifier for your application</li>
        <li><strong>Secret Key</strong>: Private key used to sign requests</li>
        <li><strong>JWT Tokens</strong>: JSON Web Tokens for session management</li>
      </ul>
      <p>Request Example:</p>
      <pre><code>curl -X GET https://api.aitradingsystem.com/v1/strategies \\n  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\n  -H "Content-Type: application/json"</code></pre>
      
      <h3>Core Endpoints</h3>
      
      <h4>Strategy Endpoints</h4>
      <ul>
        <li><code>GET /v1/strategies</code> - List all strategies</li>
        <li><code>GET /v1/strategies/{id}</code> - Get strategy details</li>
        <li><code>POST /v1/strategies</code> - Create new strategy</li>
        <li><code>PUT /v1/strategies/{id}</code> - Update strategy</li>
        <li><code>DELETE /v1/strategies/{id}</code> - Delete strategy</li>
        <li><code>POST /v1/strategies/{id}/start</code> - Start strategy</li>
        <li><code>POST /v1/strategies/{id}/stop</code> - Stop strategy</li>
      </ul>
      
      <h4>Trading Endpoints</h4>
      <ul>
        <li><code>GET /v1/positions</code> - List all open positions</li>
        <li><code>GET /v1/orders</code> - List all orders</li>
        <li><code>POST /v1/orders</code> - Place new order</li>
        <li><code>PUT /v1/orders/{id}</code> - Modify order</li>
        <li><code>DELETE /v1/orders/{id}</code> - Cancel order</li>
      </ul>
      
      <h4>Data Endpoints</h4>
      <ul>
        <li><code>GET /v1/market-data/{symbol}</code> - Get market data for symbol</li>
        <li><code>GET /v1/history/{symbol}</code> - Get historical price data</li>
        <li><code>GET /v1/indicators/{symbol}</code> - Get technical indicator values</li>
      </ul>
      
      <h3>Webhooks</h3>
      <p>The system can send webhook notifications for various events:</p>
      <ul>
        <li><strong>Order Events</strong>: Order placed, filled, modified, canceled</li>
        <li><strong>Trade Signals</strong>: New trade signals from strategies</li>
        <li><strong>System Alerts</strong>: Connection issues, balance thresholds, etc.</li>
      </ul>
      <p>Webhook payloads are sent as JSON to your configured endpoint.</p>
      
      <h3>Rate Limits</h3>
      <p>To ensure system stability, API requests are subject to rate limits:</p>
      <ul>
        <li><strong>Standard Tier</strong>: 120 requests per minute</li>
        <li><strong>Premium Tier</strong>: 600 requests per minute</li>
        <li><strong>Enterprise Tier</strong>: Custom limits available</li>
      </ul>
      <p>Rate limit headers are included in all API responses.</p>
      
      <h3>Error Handling</h3>
      <p>API errors follow standard HTTP status codes with detailed error messages:</p>
      <pre><code>{
  "error": {
    "code": "invalid_order",
    "message": "Order price must be greater than zero",
    "status": 400
  }
}</code></pre>
      
      <h3>SDK Libraries</h3>
      <p>Official SDK libraries are available for:</p>
      <ul>
        <li><strong>Python</strong>: <code>pip install aitradingsystem</code></li>
        <li><strong>JavaScript</strong>: <code>npm install aitradingsystem-js</code></li>
        <li><strong>Go</strong>: <code>go get github.com/aitradingsystem/go-client</code></li>
      </ul>
    `
  },
  'creating-strategies': {
    title: 'Creating Trading Strategies',
    content: `
      <h2>Creating Trading Strategies</h2>
      <p>Learn how to build effective trading strategies using the Strategy Builder interface.</p>
      
      <h3>Basic Strategy Structure</h3>
      <p>Every strategy should include these essential components:</p>
      <ol>
        <li><strong>Data Input:</strong> Market data, news feeds, or other information sources</li>
        <li><strong>Analysis:</strong> Technical or fundamental analysis to process the input data</li>
        <li><strong>Signal Generation:</strong> Logic to identify trading opportunities</li>
        <li><strong>Risk Management:</strong> Rules to manage position sizing and risk</li>
        <li><strong>Execution:</strong> Logic to execute trades with a broker</li>
      </ol>
      
      <h3>Building Your First Strategy</h3>
      <p>Start with a template or blank canvas, add nodes, connect them logically, and configure each node's parameters.</p>
      
      <h3>Advanced Strategy Techniques</h3>
      <ul>
        <li><strong>Multi-timeframe Analysis:</strong> Use multiple Technical Analyst nodes with different timeframes</li>
        <li><strong>Hybrid Analysis:</strong> Combine technical and fundamental analysis nodes</li>
        <li><strong>Conditional Execution:</strong> Create different paths based on market conditions</li>
      </ul>
    `
  },
  'openai-integration': {
    title: 'OpenAI Integration',
    content: `
      <h2>OpenAI Integration</h2>
      <p>Learn how to leverage OpenAI's capabilities to enhance your trading strategies.</p>
      
      <h3>Setting Up OpenAI</h3>
      <ol>
        <li>Obtain an API key from OpenAI</li>
        <li>In the AI Trading System, go to Settings → API Keys</li>
        <li>Enter your OpenAI API key</li>
        <li>Click "Test Connection" to verify it works</li>
      </ol>
      
      <h3>Configuring Assistant IDs</h3>
      <p>Each AI node requires its own OpenAI Assistant ID:</p>
      <ol>
        <li>Create assistants in the OpenAI platform with appropriate instructions</li>
        <li>Copy the Assistant IDs from your OpenAI dashboard</li>
        <li>In each AI node, enter the corresponding Assistant ID</li>
        <li>Use the "Test Connection" button to verify the assistant works</li>
      </ol>
      
      <h3>Effective Prompt Engineering</h3>
      <p>Tips for creating effective prompts for your trading assistants:</p>
      <ul>
        <li><strong>Be Specific:</strong> Clearly define what indicators or patterns you want analyzed</li>
        <li><strong>Include Reasoning:</strong> Ask the assistant to explain its analysis process</li>
        <li><strong>Structure Output:</strong> Request a specific format for signals (e.g., BUY/SELL/HOLD)</li>
      </ul>
    `
  },
  'introduction': {
    title: 'Introduction to AI Trading System',
    content: `
      <h2>Welcome to the AI Trading System</h2>
      <p>The AI Trading System is a comprehensive platform designed to harness the power of artificial intelligence for financial trading. This platform integrates with various brokers, provides technical and fundamental analysis tools, and offers a modular strategy builder for creating sophisticated trading strategies.</p>
      
      <h3>Key Features</h3>
      <ul>
        <li><strong>AI-Powered Analysis:</strong> Leverage OpenAI's capabilities for technical and fundamental market analysis</li>
        <li><strong>Strategy Builder:</strong> Create complex trading strategies with a visual node-based system</li>
        <li><strong>Multi-Broker Support:</strong> Connect to various exchanges and brokers simultaneously</li>
        <li><strong>Risk Management:</strong> Sophisticated risk management tools to protect your investments</li>
        <li><strong>Trade Execution:</strong> Automatic or semi-automatic trade execution based on your strategies</li>
        <li><strong>Performance Tracking:</strong> Detailed trade history and performance analytics</li>
        <li><strong>Reporting:</strong> Automated reports via Telegram and other channels</li>
      </ul>
      
      <p>This documentation will guide you through all aspects of the platform, from basic setup to advanced strategy development.</p>
    `
  },
  'installation': {
    title: 'Installation Guide',
    content: `
      <h2>Installation</h2>
      <p>Follow these steps to install and set up the AI Trading System:</p>
      
      <h3>System Requirements</h3>
      <ul>
        <li>Node.js 16.x or later</li>
        <li>NPM 8.x or later</li>
        <li>Modern web browser (Chrome, Firefox, Edge)</li>
        <li>Minimum 4GB RAM (8GB recommended)</li>
        <li>Internet connection for API integrations</li>
      </ul>
      
      <h3>Installation Steps</h3>
      <h4>1. Clone the repository:</h4>
      <pre><code>git clone https://github.com/your-username/ai-trading-system.git
cd ai-trading-system</code></pre>
      
      <h4>2. Install dependencies:</h4>
      <pre><code>npm install</code></pre>
      
      <h4>3. Set up environment variables:</h4>
      <p>Create a .env file in the root directory with the following variables:</p>
      <pre><code>REACT_APP_OPENAI_API_KEY=your_openai_api_key
REACT_APP_API_BASE_URL=http://localhost:5000/api</code></pre>
      
      <h4>4. Start the development server:</h4>
      <pre><code>npm start</code></pre>
      
      <p>The application will be available at <a href="http://localhost:3000">http://localhost:3000</a></p>
      
      <h3>Docker Installation (Alternative)</h3>
      <p>If you prefer using Docker:</p>
      <pre><code>docker-compose up</code></pre>
      
      <p>The application will be available at <a href="http://localhost:3000">http://localhost:3000</a></p>
    `
  },
  'nodes-overview': {
    title: 'Strategy Builder Nodes Overview',
    content: `
      <h2>Strategy Builder Nodes</h2>
      <p>The Strategy Builder uses a node-based system to create trading strategies. Each node represents a component with specific functionality that can be connected to other nodes to create a complete trading workflow.</p>
      
      <h3>Node Types</h3>
      
      <h4>Technical Analyst Node</h4>
      <p>Analyzes market data using technical indicators such as moving averages, RSI, MACD, etc. Powered by OpenAI assistance for pattern recognition.</p>
      <ul>
        <li><strong>Inputs:</strong> Market data</li>
        <li><strong>Outputs:</strong> Technical signals</li>
        <li><strong>Configuration:</strong> Symbol, timeframe, indicators, OpenAI assistant ID</li>
      </ul>
      
      <h4>Fundamental Analyst Node</h4>
      <p>Analyzes news, social media sentiment, and economic data to generate trading signals based on fundamental factors.</p>
      <ul>
        <li><strong>Inputs:</strong> News feeds, economic data</li>
        <li><strong>Outputs:</strong> Fundamental signals</li>
        <li><strong>Configuration:</strong> Data sources, update frequency, sentiment thresholds</li>
      </ul>
      
      <h4>Risk Manager Node</h4>
      <p>Evaluates trading signals against risk parameters to determine position sizing and risk management rules.</p>
      <ul>
        <li><strong>Inputs:</strong> Trading signals</li>
        <li><strong>Outputs:</strong> Risk-adjusted orders</li>
        <li><strong>Configuration:</strong> Maximum risk per trade, leverage, portfolio allocation</li>
      </ul>
      
      <h4>Execution Layer Node</h4>
      <p>Handles the execution of trades through connected brokers.</p>
      <ul>
        <li><strong>Inputs:</strong> Risk-adjusted orders</li>
        <li><strong>Outputs:</strong> Executed trades</li>
        <li><strong>Configuration:</strong> Execution mode, confirmation requirements, trading parameters</li>
      </ul>
      
      <h4>Memory Node</h4>
      <p>Stores and retrieves trading information for historical analysis and strategy refinement.</p>
      <ul>
        <li><strong>Inputs:</strong> Any node output</li>
        <li><strong>Outputs:</strong> Stored data</li>
        <li><strong>Configuration:</strong> Storage type, retention period</li>
      </ul>
      
      <h4>Telegram Report Node</h4>
      <p>Sends automated reports and alerts to Telegram channels or chats.</p>
      <ul>
        <li><strong>Inputs:</strong> Trading data, signals, or executed trades</li>
        <li><strong>Outputs:</strong> None (external communication)</li>
        <li><strong>Configuration:</strong> Bot token, chat ID, report frequency, content types</li>
      </ul>
    `
  },
  'alpaca': {
    title: 'Alpaca Integration',
    content: `
      <h2>Alpaca Integration</h2>
      <p>Alpaca is a commission-free API-first broker that offers stocks, ETFs, and crypto trading. The AI Trading System provides seamless integration with Alpaca for executing trades.</p>
      
      <h3>Setting Up Alpaca</h3>
      <ol>
        <li>Create an account at <a href="https://alpaca.markets/" target="_blank">Alpaca Markets</a></li>
        <li>Navigate to "Paper Trading" if you want to test without real money</li>
        <li>Go to API Keys in your Dashboard</li>
        <li>Generate a new API key pair</li>
        <li>Copy the API Key ID and Secret Key</li>
      </ol>
      
      <h3>Configuration in AI Trading System</h3>
      <ol>
        <li>Go to Settings → Brokers → Alpaca</li>
        <li>Enter your API Key and Secret Key</li>
        <li>Toggle "Use Paper Trading" if you're testing</li>
        <li>Click "Test Connection" to verify your credentials</li>
      </ol>
      
      <h3>Available Features</h3>
      <ul>
        <li>US Stocks and ETFs trading</li>
        <li>Crypto trading (selected pairs)</li>
        <li>Market data access</li>
        <li>Limit, market, and stop orders</li>
        <li>Fractional shares</li>
        <li>Extended hours trading</li>
      </ul>
      
      <h3>Limitations</h3>
      <ul>
        <li>Only US markets for stocks</li>
        <li>Limited crypto selection compared to dedicated crypto exchanges</li>
        <li>API rate limits apply</li>
      </ul>
      
      <h3>Troubleshooting</h3>
      <p>If you encounter connection issues:</p>
      <ul>
        <li>Verify your API keys are correct</li>
        <li>Check if your account is restricted or under review</li>
        <li>Ensure you're not exceeding API rate limits</li>
        <li>Check Alpaca's status page for service disruptions</li>
      </ul>
    `
  }
};

function Documentation() {
  const [selectedSection, setSelectedSection] = useState('introduction');
  const [openSections, setOpenSections] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Find the current section and subsection
  const currentMainSection = docSections.find(section => 
    section.subsections.some(subsection => subsection.id === selectedSection)
  );
  
  const currentSubsection = currentMainSection?.subsections.find(
    subsection => subsection.id === selectedSection
  );

  // Initialize open sections object when component loads
  React.useEffect(() => {
    // Find which section contains the selected subsection and open it
    if (currentMainSection && !openSections[currentMainSection.id]) {
      setOpenSections(prev => ({
        ...prev,
        [currentMainSection.id]: true
      }));
    }
  }, [selectedSection, currentMainSection, openSections]);

  // Handle toggle for section collapse
  const handleToggle = (sectionId) => {
    setOpenSections({
      ...openSections,
      [sectionId]: !openSections[sectionId]
    });
  };

  // Handle selecting a documentation section
  const handleSelectSection = (sectionId) => {
    setSelectedSection(sectionId);
    // If on mobile, close the drawer after selection
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{
        mb: 3, display: 'flex', alignItems: 'center',
        p: 2, borderRadius: 3,
        background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <MenuBookIcon sx={{ fontSize: 28, mr: 1, color: '#00FF94' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
          Documentation
        </Typography>
        {isMobile && (
          <IconButton 
            onClick={() => setDrawerOpen(!drawerOpen)} 
            sx={{ ml: 'auto', color: '#9CA3AF' }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="#" onClick={() => {}}>
          Documentation
        </Link>
        {currentMainSection && (
          <Link color="inherit" href="#" onClick={() => {}}>
            {currentMainSection.title}
          </Link>
        )}
        {currentSubsection && (
          <Typography color="text.primary">{currentSubsection.title}</Typography>
        )}
      </Breadcrumbs>

      <Grid container spacing={3} sx={{ flexGrow: 1 }}>
        {/* Left sidebar */}
        <Grid 
          item 
          xs={12} 
          md={3} 
          sx={{ 
            display: isMobile ? (drawerOpen ? 'block' : 'none') : 'block',
            position: isMobile ? 'absolute' : 'relative',
            zIndex: isMobile ? 10 : 1,
            width: isMobile ? '100%' : 'auto',
            height: isMobile ? '100%' : 'auto',
            top: isMobile ? 0 : 'auto',
            left: isMobile ? 0 : 'auto',
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Paper 
            sx={{ 
              p: 0, 
              height: '100%', 
              borderRadius: 0,
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 180px)'
            }}
          >
            <List component="nav" dense>
              {docSections.map((section) => (
                <React.Fragment key={section.id}>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleToggle(section.id)}>
                      <ListItemIcon>
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText primary={section.title} />
                      {openSections[section.id] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={openSections[section.id] || false} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {section.subsections.map((subsection) => (
                        <ListItem key={subsection.id} disablePadding>
                          <ListItemButton 
                            sx={{ pl: 4 }}
                            selected={selectedSection === subsection.id}
                            onClick={() => handleSelectSection(subsection.id)}
                          >
                            <ListItemText primary={subsection.title} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Main content */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 3, height: '100%', overflowY: 'auto', maxHeight: 'calc(100vh - 180px)' }}>
            {docContent[selectedSection] ? (
              <>
                <Typography variant="h4" component="h1" gutterBottom>
                  {docContent[selectedSection].title}
                </Typography>
                <Box 
                  dangerouslySetInnerHTML={{ __html: docContent[selectedSection].content }} 
                  sx={{
                    '& pre': {
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      p: 2,
                      borderRadius: 1,
                      overflowX: 'auto'
                    },
                    '& code': {
                      fontFamily: 'monospace'
                    },
                    '& h2': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      pb: 1,
                      mb: 2
                    },
                    '& ul, & ol': {
                      pl: 3
                    }
                  }}
                />
              </>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="h5" component="h2">
                    Documentation in Progress
                  </Typography>
                  <Typography variant="body1">
                    This section is currently being developed. Please check back later for updates.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Documentation;
